/**
 * Audit event → item timeline vocabulary.
 *
 * One mapping, two consumers: the item lineage timeline
 * (ItemLineageTimeline.tsx) and the Item Journey page (ItemJourneyPage.tsx).
 * Both used to carry their own short allowlist and silently `continue` past
 * anything not on it, so the two most common item events in the whole system —
 * `item_category_updated` (774 rows / 14 workspaces) and `item_altitude_updated`
 * (659 / 15) — never reached either screen. An item with a real history showed
 * a single "Created" entry.
 *
 * Canon (01-terminology-and-glossary.md → Lineage) says Lineage is *curated*:
 * meaningful events across five lanes (Identity, Cycles, Sessions,
 * Relationships, Lifecycle), with Item History as the complete log below it.
 * So the answer is not "show every audit row" — it is to decide each type
 * once, here, in the open: mapped, or deliberately ignored as noise.
 * KNOWN_AUDIT_EVENT_TYPES + the test over it make a new unclassified type a
 * test failure rather than another silent drop.
 */

/** Neutral vocabulary; each screen translates these into its own event union. */
export type ItemEventKind =
  | 'created'
  | 'imported'
  | 'shaping_started'
  | 'shaping_completed'
  | 'leadership_approved'
  | 'promoted'
  | 'archived'
  | 'deferred'
  | 'reactivated'
  | 'completed'
  | 'retired'
  | 'classification_changed'
  | 'parent_changed'
  | 'dedupe_reviewed'
  | 'pool_added'
  | 'cycle_added'
  | 'relationship_added'
  | 'relationship_dissolved'
  | 'decision_recorded'
  | 'lineage_split'
  | 'lineage_fork'
  // Any other recorded change to the item (a field, a value, evidence…). The
  // timeline shows every change the History log shows (owner, 2026-10-15).
  | 'item_changed';

/** Direct type → kind. Case-sensitive; legacy SHOUTING spellings listed explicitly. */
const DIRECT: Record<string, ItemEventKind> = {
  item_created: 'created',
  CREATE: 'created',
  item_imported: 'imported',
  candidates_imported_from_ost: 'imported',
  shaping_started: 'shaping_started',
  shaping_completed: 'shaping_completed',
  item_promoted: 'promoted',
  PROMOTE: 'promoted',
  ITEM_PROMOTED: 'promoted',
  item_marked_complete: 'completed',
  item_deleted: 'retired',

  // The two most common item events in production, dropped until now.
  item_category_updated: 'classification_changed',
  item_altitude_updated: 'classification_changed',
  item_work_type_updated: 'classification_changed',
  altitude_set: 'classification_changed',

  item_parent_updated: 'parent_changed',
  item_dedupe_updated: 'dedupe_reviewed',

  ItemPoolItemAdded: 'pool_added',
  item_added_to_cycle: 'cycle_added',

  relationship_edge_added: 'relationship_added',
  elaboration_declared: 'relationship_added',
  cluster_flagged: 'relationship_added',
  relationship_edge_dissolved: 'relationship_dissolved',

  'decision.completed': 'decision_recorded',

  item_split: 'lineage_split',
  lineage_split: 'lineage_split',
  lineage_fork: 'lineage_fork',
};

/**
 * Types deliberately kept off the timeline. Canon: Lineage surfaces meaningful
 * events, not every field edit — these belong to Item History.
 * Listed, not dropped by omission, so the decision is reviewable.
 */
export const IGNORED_AUDIT_EVENT_TYPES: ReadonlySet<string> = new Set([
  // Field edits and value changes used to be hidden here as "noise at this altitude".
  // They are shown now, as item_changed, so the timeline and the History log match
  // (owner, 2026-10-15). What stays hidden is what the log does not show either.
  // Analytics / telemetry.
  'app.first_open',
  'pattern_detected',
  // Rows written with an empty event_type (4 in production, one workspace).
  '',
]);

/**
 * Every `audit_events.event_type` observed in production carrying an item_id,
 * as of 2026-09-30. Refresh with:
 *
 *   select event_type, count(*) from audit_events
 *   where item_id is not null group by event_type order by 2 desc;
 *
 * The test asserts each one is either mapped or explicitly ignored — so a new
 * type shows up as a failing test instead of an event nobody can see.
 */
export const KNOWN_AUDIT_EVENT_TYPES: readonly string[] = [
  'item_category_updated',
  'item_work_type_updated',
  'item_tags_updated',
  'item_launch_type_updated',
  'item_investment_type_updated',
  'item_field_updated',
  'shaping_started',
  'item_altitude_updated',
  'VALUE_ACCEPTED',
  'item_title_updated',
  'item_deleted',
  'PROPOSAL_SUBMITTED',
  'lock',
  'value_acceptance',
  'item_promoted',
  'item_transition',
  'relationship_edge_added',
  'ItemPoolItemAdded',
  'item_dedupe_updated',
  'item_imported',
  'item_created',
  'altitude_set',
  'ItemPoolUpdated',
  'ValueLocked',
  'item_description_updated',
  'relationship_edge_dissolved',
  'ITEM_PROMOTED',
  'value_proposal',
  'item_parent_updated',
  'item_marked_complete',
  '',
  'pattern_detected',
  'item_added_to_cycle',
  'item_split',
  'lineage_split',
  'lineage_fork',
  'VALUE_REJECTED',
  'cluster_flagged',
  'elaboration_declared',
  'decision.completed',
  'app.first_open',
];

/** State-transition events carry their meaning in `toState`. */
function transitionToKind(toState?: string, fromState?: string): ItemEventKind | null {
  const st = (toState ?? '').toLowerCase();
  if (!st) return null;
  // Back to active from deferred or archived. The writer fills a missing prior
  // state with 'active', so active → active is not a change worth showing.
  if (st === 'active') return (fromState ?? '').toLowerCase() === 'active' ? null : 'reactivated';
  if (st === 'archived' || st.includes('archiv')) return 'archived';
  if (st === 'deferred' || st.includes('defer')) return 'deferred';
  if (st.includes('approved') || st.includes('leadership')) return 'leadership_approved';
  if (st === 'promoted' || st === 'ranking' || st === 'prioritized') return 'promoted';
  if (st === 'shaping' || st === 'in_shaping') return 'shaping_started';
  if (st === 'shaped' || st === 'shaping_completed' || st === 'ready') return 'shaping_completed';
  if (st === 'complete' || st === 'completed' || st === 'done') return 'completed';
  if (st === 'retired' || st.includes('retire')) return 'retired';
  return null;
}

/** Map one audit row to a timeline kind, or null when it is not timeline-worthy. */
export function auditToItemEvent(eventType: string, toState?: string, fromState?: string): ItemEventKind | null {
  const direct = DIRECT[eventType];
  if (direct) return direct;
  if (eventType === 'item_transition' || eventType === 'STATE_CHANGE') {
    return transitionToKind(toState, fromState);
  }
  if (IGNORED_AUDIT_EVENT_TYPES.has(eventType)) return null;
  return 'item_changed';
}

/**
 * A record of the cycle the item sits in, not of the item: a cut line moved, a
 * ball claimed, a track locked. The audit store tags these (get_item_cycle_trail)
 * so the History log can say "cycle activity". On an item's timeline they are
 * someone else's work — the Sightline SOC 2 item drew 58 of them as "Changed".
 * The timeline's Cycles lane links to the cycle, whose history holds them.
 */
export function isCycleActivity(entry: { payload?: Record<string, unknown> | null }): boolean {
  return entry.payload?._source === 'cycle';
}

/** True when this type is known and deliberately not shown. */
export function isIgnoredAuditEvent(eventType: string): boolean {
  return IGNORED_AUDIT_EVENT_TYPES.has(eventType);
}

/** Exposed for the coverage test. */
export function isMappedAuditEvent(eventType: string): boolean {
  // A state transition maps by its states, so it counts as mapped without them.
  return eventType === 'item_transition' || eventType === 'STATE_CHANGE' || auditToItemEvent(eventType) !== null;
}

// ─── Session membership ───────────────────────────────────────────────────────

interface SessionLike {
  itemIds?: string[] | null;
  rankingOutput?: {
    rankedItemIds?: string[];
    orderedItems?: { id: string }[];
    scores?: Record<string, unknown>;
  } | null;
}

/**
 * Did this session involve this item?
 *
 * `itemIds` alone is wrong: a session fed from an item pool persists an empty
 * `item_ids` and records membership in its ranking output (and in
 * `session_results`). The Sightline demo's completed Q4 ranking had 18 results,
 * a full `rankedItemIds`, and an empty `item_ids` — so every item it ranked
 * showed no session at all on its timeline.
 */
export function sessionIncludesItem(session: SessionLike, itemId: string): boolean {
  if (session.itemIds?.includes(itemId)) return true;
  const ro = session.rankingOutput;
  if (!ro) return false;
  if (ro.rankedItemIds?.includes(itemId)) return true;
  if (ro.orderedItems?.some((i) => i?.id === itemId)) return true;
  if (ro.scores && Object.prototype.hasOwnProperty.call(ro.scores, itemId)) return true;
  return false;
}

/**
 * A field edit, as a timeline entry should title it. The audit store's change
 * summary reads "Category: Problem → Opportunity"; a node labelled only
 * "Classification" does not say what happened, so the field becomes the title
 * and the values the detail. A first value reads "Category set" / "Problem".
 */
export function fieldChangeTitle(summary?: string | null): { label: string; detail: string } | null {
  if (!summary) return null;
  const match = /^([^:]+): (.+) → (.+)$/.exec(summary);
  if (!match) return null;
  const [, field, from, to] = match;
  if (from === '—') return { label: `${field} set`, detail: to };
  if (to === '—') return { label: `${field} cleared`, detail: `was ${from}` };
  return { label: `${field} changed`, detail: `${from} → ${to}` };
}
